import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ModerationActions, ModerationTransitions } from '../../../../../../models';

import { Button, SelectHierarchyLabel, Message, Section } from '@weezevent/nacre';

import { ActionOption, SelectedAction } from '../../components';
import ActionModal from '../../actions/modal';

import css from '../transitionDetails.module.css';

export default function ActionsStep({ form, setForm, onCardDone, flowId, organization, transitionExists }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [querySearch, setQuerySearch] = React.useState('');
    const [actionModal, setActionModal] = React.useState({ open: false, id: null });

    const [transitionActions, loadingTransitionActions, syncTransitionActions] = ModerationActions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            transitionId: form.id,
            cache: false,
            allow_cache: false,
            query: {
                search: querySearch
            }
        },
        [flowId, organization.id, querySearch]
    );

    const actions = React.useMemo(() => {
        return form.actions.map(ac => new ModerationActions(ac));
    }, [form.actions]);

    const placeHolder = React.useMemo(() => {
        if (!form.actions.length) {
            return t('ticket.common.action.select');
        }
        return t('ticket.moderation.statusesTransitions.transition.steps.actions.selectedAction', { count: form.actions.length });
    }, [form.actions]);

    const actionsOptions = React.useCallback(
        (actions = []) => {
            if (loadingTransitionActions || !actions) {
                return [];
            }

            return actions
                ?.filter(ac => !form.actions.some(a => a.id === ac.id))
                .map(act => ({
                    key: act.id,
                    text: <ActionOption action={act} />,
                    value: act.id,
                    item: act
                }));
        },
        [loadingTransitionActions, form.actions]
    );

    const handleChange = React.useCallback(
        item => {
            setForm('actions', [...form.actions, item]);
        },
        [form.actions]
    );

    const handleDelete = React.useCallback(
        pk => {
            setForm('actions', [...form.actions.filter(ac => ac.id !== pk)]);
        },
        [form.actions]
    );

    const handleRedirect = React.useCallback(
        pk => {
            window.open(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/actions/${pk}`, '_blank').focus();
        },
        [organization.id, flowId]
    );

    const handleSubmit = React.useCallback(
        (isQuitting = false) => {
            let transition = new ModerationTransitions({ ...form });
            transition.actions = form.actions.map(ac => ac.id);
            transition.conditions = form.conditions.map(cond => cond.id);
            transition.save({ organizationId: organization.id, flowId }).catch(() => {
                setIsSubmitting(false);
                return;
            });

            if (isQuitting) {
                history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
            } else {
                onCardDone(true);
            }
        },
        [form, organization.id, flowId]
    );

    return (
        <>
            <Message
                type="default"
                title={t('ticket.moderation.statusesTransitions.transition.steps.actions.title')}
                links={{
                    label: t('ticket.moderation.statusesTransitions.transition.steps.actions.create'),
                    onClick: () => setActionModal({ open: true, id: null })
                }}
            >
                {t('ticket.moderation.statusesTransitions.transition.steps.actions.message')}
            </Message>
            <ActionModal organization={organization} flowId={flowId} state={actionModal} onClose={() => setActionModal({ open: false, id: null })} sync={syncTransitionActions} />
            <div className={css['selector-container']}>
                <SelectHierarchyLabel
                    height="medium"
                    placeholder={placeHolder}
                    searchValue={querySearch}
                    onExternalSearch={setQuerySearch}
                    options={actionsOptions(transitionActions)}
                    externalOptions={actionsOptions(transitionActions)}
                    placeholderSearch={t('ticket.common.action.search')}
                    onChange={(_, { item }) => handleChange(item)}
                    label={t('ticket.common.action.actions')}
                />
            </div>

            {!!actions.length && (
                <Section className={css['action-section']}>
                    {actions?.map(selectedAction => (
                        <SelectedAction
                            key={selectedAction.id}
                            action={selectedAction}
                            status={false}
                            handleDelete={({ pk }) => handleDelete(pk)}
                            handleEdit={({ pk }) => handleRedirect(pk)}
                        />
                    ))}
                </Section>
            )}

            {!transitionExists && (
                <Button.Group>
                    <Button
                        primary
                        label={t('ticket.moderation.statusesTransitions.transition.steps.settings.saveAndContinue')}
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                    />
                    <Button
                        primary
                        inverted
                        label={t(`ticket.moderation.statusesTransitions.transition.steps.settings.saveAndQuit`)}
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitting}
                    />
                </Button.Group>
            )}
        </>
    );
}
